import React, { useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { GameObject, DEFAULT_TAGS, DEFAULT_LAYERS } from '../../../core/types/gameobject.types';
import { useSceneStore } from '../../store/sceneStore';
import { cn } from '../../utils/cn';

interface GameObjectSectionProps {
  gameObject: GameObject;
}

export const GameObjectSection: React.FC<GameObjectSectionProps> = ({ gameObject }) => {
  const { updateGameObject } = useSceneStore();

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateGameObject(gameObject.id, { name: e.target.value });
    },
    [gameObject.id, updateGameObject]
  );

  const handleTagChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateGameObject(gameObject.id, { tag: e.target.value });
    },
    [gameObject.id, updateGameObject]
  );

  const handleLayerChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateGameObject(gameObject.id, { layer: e.target.value });
    },
    [gameObject.id, updateGameObject]
  );

  const handleActiveChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateGameObject(gameObject.id, { active: e.target.checked });
    },
    [gameObject.id, updateGameObject]
  );

  return (
    <div className="p-3 border-b border-zinc-800">
      {/* Name and Active checkbox */}
      <div className="flex items-center gap-2 mb-3">
        <input
          type="checkbox"
          checked={gameObject.active}
          onChange={handleActiveChange}
          className={cn(
            'w-4 h-4 rounded border-zinc-600 bg-zinc-800',
            'text-blue-500 focus:ring-blue-500 focus:ring-offset-0',
            'cursor-pointer'
          )}
          title={gameObject.active ? 'Active' : 'Inactive'}
        />
        <input
          type="text"
          value={gameObject.name}
          onChange={handleNameChange}
          className={cn(
            'flex-1 px-2 py-1.5 text-sm bg-zinc-800 border border-zinc-700 rounded',
            'text-zinc-100 font-medium focus:outline-none focus:border-blue-500'
          )}
          placeholder="GameObject Name"
        />
      </div>

      {/* Tag and Layer dropdowns */}
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-zinc-500">Tag</label>
          <div className="relative">
            <select
              value={gameObject.tag}
              onChange={handleTagChange}
              className={cn(
                'w-full px-2 py-1.5 pr-8 text-xs bg-zinc-800 border border-zinc-700 rounded',
                'text-zinc-300 focus:outline-none focus:border-blue-500',
                'appearance-none cursor-pointer'
              )}
            >
              {DEFAULT_TAGS.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-zinc-500">Layer</label>
          <div className="relative">
            <select
              value={gameObject.layer}
              onChange={handleLayerChange}
              className={cn(
                'w-full px-2 py-1.5 pr-8 text-xs bg-zinc-800 border border-zinc-700 rounded',
                'text-zinc-300 focus:outline-none focus:border-blue-500',
                'appearance-none cursor-pointer'
              )}
            >
              {DEFAULT_LAYERS.map((layer) => (
                <option key={layer} value={layer}>
                  {layer}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
